const aroQuestions = {
	1: {
		question: 'Do you have a legal obligation to retire an asset?',
		progress: 1,
		info: {
			infoAbbreviated: `
				<b>Common Examples</b><br />
				<ul>
					<li>A government action - law, statue, or ordinance</li>
					<li>An agreement between parties - lease agreement, operating permit, or verbal agreement</li>
				</ul>
				`,
			infoExpanded:
				'<b>Analysis</b><br /> The guidance in ASC 410-20 applies to legal obligations associated with the retirement of a tangible long-lived asset. If you are not a party to, or subject to, a legal obligation, you would not have an asset retirement obligation.',
		},
		navigation: {
			navYes: '/aro-identifier/prompt/3',
			navNo: '/aro-identifier/prompt/2',
			current: 1,
		},
	},
	2: {
		question:
			'Do you have a legal obligation to restore an asset to its original condition?',
		progress: 2,
		navigation: {
			navYes: '/aro-identifier/prompt/3',
			navNo: '/aro-identifier/submit',
		},
	},
	3: {
		question: 'Does the legal obligation involve a tangible long-lived asset?',
		progress: 3,
		info: {
			infoAbbreviated: `
				<b>Common Examples</b><br />
				<ul>
					<li>Land</li>
					<li>Equipment</li>
					<li>Buildings</li>
					<li>Leasehold improvements</li>
				</ul>
				`,
			infoExpanded:
				'<b>Analysis</b><br /> The guidance in ASC 410-20 applies to legal obligations associated with the retirement of a tangible long-lived asset. If you are not a party to, or subject to, a legal obligation, you would not have an asset retirement obligation.',
		},
		navigation: {
			navYes: '/aro-identifier/prompt/4',
			navNo: '/aro-identifier/submit',
		},
	},
	4: {
		question:
			'Did you make additions, improvements, or customizations to the asset that you are obligated to remove in order to return the asset to its previous state?',
		progress: 4,
		info: {
			infoAbbreviated:
				'<b>Common Examples</b><br /> An oil and gas company using land for oil transport added an underground storage tank under surface land used for petroleum storage. The addition of the underground storage tank causes an asset retirement obligation for the cost of removing that tank, if within the terms of the agreement, they are required to remove it.<br />',
			infoExpanded:
				'As another example, assume a retail company leases a building space to use as a store or facility location. The lease also states that any improvements must be removed at the end of the lease and that the property must be returned to its original condition. If the lessee adds equipment, paints specific to their trademark colors, puts in custom fixtures, or even takes out some of the walls within the office space, the obligation to remove and restore the improvements falls on the lessee. The cost to remove and restore the building, in this example, is an asset retirement obligation. <br /><br />Another example is a telecom company that has an agreement with a local company to use their land to put up towers. The agreement explicitly states that, at the end of the term, the telecom company is solely responsible for removing the equipment and all costs to restore the surrounding area. In this example, this is an ARO. The following are additional examples of common AROs associated with the energy and utility industry: mine reclamation, nuclear commissioning, oil well plugging and pipeline abandonment, property restoration caused by the construction of trash facilities, tower removal (drilling and network), etc. <br /><br /><b>Analysis</b><br /> If a company enters into an agreement, constructs improvements, and determines based on the provisions of the agreement that it is legally obligated to remove the improvements at the end of the agreement, in accordance with ASC 410-20, the company may have an ARO.',
		},
		navigation: {
			navYes: '/aro-identifier/prompt/5',
			navNo: '/aro-identifier/submit',
		},
	},
	5: {
		question:
			'If you are a lessee, at the end of the lease term, are you required to remove modifications that you made to the leased asset so that the asset is returned to the lessor in its original condition?',
		progress: 5,
		info: {
			infoAbbreviated:
				"<b>Common Example</b><br /> An industrial gas production and distribution company leases land and installs several large underground storage tanks (lessee-installed leasehold improvements) on the site. Because the underground storage tanks were installed by lessee, the lessee's obligation to remove the tanks and restore the site to its original condition is an asset retirement obligation <br />",
			infoExpanded:
				'<b>Analysis</b><br /> Generally, the removal of lessee-installed leasehold improvements, such as the underground storage tanks in the example above, are considered asset retirement obligations. However, if within the lease agreement, it is required that the lessee either remove or dismantle the original underlying asset, these estimated costs are not an asset retirement obligation and should be included in the lease payments. This means they should be accounted for as part of the initial lease liability and right-of-use asset calculations if reporting under ASC 842, and as part of the minimum lease payments if reporting under ASC 840.',
		},
		navigation: {
			navYes: '/aro-identifier/prompt/6',
			navNo: '/aro-identifier/submit',
			navNA: true,
		},
	},
	6: {
		question:
			'Are you ONLY responsible for maintaining the asset and/or preparing the asset for alternative use?',
		progress: 6,
		info: {
			infoAbbreviated:
				'<b>Common Example</b><br /> If you are an energy company and have constructed an energy distribution network, you may have required maintenance on some of the components such as the transformers. The cost and obligation associated with that maintenance is not an asset retirement obligation.<br />',
			infoExpanded:
				'<b>Analysis</b><br /> Obligations associated with the maintenance, rather than retirement, of the asset are not considered an ARO. The same is said for any activities necessary to prepare the asset for alternative use, as well as the cost of replacement parts for the asset. If the obligation is associated with maintenance, repair or preparation of the asset for alternative use, the obligation is not an ARO.',
		},
		navigation: {
			navYes: '/aro-identifier/submit',
			navNo: '/aro-identifier/prompt/7',
		},
	},
	7: {
		question:
			'Is your obligation to restore the asset to its original condition caused by improper use of the asset?',
		progress: 7,
		info: {
			infoAbbreviated:
				'<b>Common Example</b><br /> Assume that you are operating a water treatment facility. A certain amount of chemical spillage is acceptable within the normal operations of the facility. However, assume that an employee did not follow standard safety procedures, and caused a massive spillage and leak of water treatment chemicals. The obligation to clean up the surrounding land and area due to a catastrophic accident is not an asset retirement obligation and instead is an environmental remediation liability.<br />',
			infoExpanded:
				'<b>Analysis</b><br /> The obligation to clean up resulting from the normal operations of the asset is an asset retirement obligation, however, a liability obligation to clean up after an accident that results from improper use of the asset is not an asset retirement obligation and is instead treated under the guidance in ASC 410-30, not 410-20.',
		},
		navigation: {
			navYes: '/aro-identifier/submit',
			navNo: '/aro-identifier/prompt/8',
		},
	},
	8: {
		question: 'Can you reasonably estimate the cost to retire the asset?',
		progress: 8,
		info: {
			infoAbbreviated:
				'<b>Common Example</b><br /> If you have a legal obligation to remove an offshore drilling platform and you can reasonably estimate the fair value of the removal costs based on active market values for the price of removal, an ARO has been incurred.<br />',
			infoExpanded:
				'<b>Analysis</b><br /> 410-20-25-4 states that an entity shall recognize the fair value of a liability for an asset retirement obligation in the period in which it is incurred if a reasonable estimate of fair value can be made. If a reasonable estimate of fair value cannot be made in the period the asset retirement obligation is incurred, the liability shall be recognized when a reasonable estimate of fair value can be made.',
		},
		navigation: {
			navYes: '/aro-identifier/submit',
			navNo: '/aro-identifier/submit',
		},
	},
	submit: {
		question: 'Where would you like to receive results?',
		progress: 10,
	},
}

export default aroQuestions
